import { existsSync } from "node:fs";
import { join } from "node:path";
import type { AgentTool } from "@elyracode/agent-core";
import { Text } from "@elyracode/tui";
import { mkdir as fsMkdir, writeFile as fsWriteFile } from "fs/promises";
import { type Static, Type } from "typebox";
import type { ExtensionContext, ToolDefinition } from "../extensions/types.js";
import { wrapToolDefinition } from "./tool-definition-wrapper.js";

/** Max name length per Agent Skills spec */
const MAX_NAME_LENGTH = 64;
/** Max description length per Agent Skills spec */
const MAX_DESCRIPTION_LENGTH = 1024;

const skillWriteSchema = Type.Object({
	name: Type.String({
		description:
			"Skill name: lowercase letters, digits, and hyphens only (e.g. 'stripe-webhooks'). Becomes the directory name and must be unique. Max 64 chars.",
	}),
	description: Type.String({
		description:
			"One-sentence description of when this skill applies. Used by the agent to decide when to load the skill. Max 1024 chars.",
	}),
	body: Type.String({
		description:
			"The skill body in Markdown. Document the reusable solution: when to use it, the steps, gotchas, and any commands or code patterns. Write it so a future session can follow it without rediscovering the solution.",
	}),
	scope: Type.Optional(
		Type.Union([Type.Literal("user"), Type.Literal("project")], {
			description:
				"'user' saves to your global skills (available everywhere). 'project' saves into the project (committed to git, shared with the team). Default: user.",
		}),
	),
});

export type SkillWriteToolInput = Static<typeof skillWriteSchema>;

export interface SkillWriteToolDetails {
	name: string;
	scope: "user" | "project";
	path: string | null;
	saved: boolean;
	rejected?: boolean;
}

/** Pluggable operations for the skill-write tool (overridable for tests). */
export interface SkillWriteOperations {
	writeFile: (absolutePath: string, content: string) => Promise<void>;
	mkdir: (dir: string) => Promise<void>;
	exists: (path: string) => boolean;
}

const defaultSkillWriteOperations: SkillWriteOperations = {
	writeFile: (path, content) => fsWriteFile(path, content, "utf-8"),
	mkdir: (dir) => fsMkdir(dir, { recursive: true }).then(() => {}),
	exists: (path) => existsSync(path),
};

export interface SkillWriteToolOptions {
	/** Directory for user-scoped skills (e.g. ~/.elyra/agent/skills). */
	userSkillsDir: string;
	/** Directory for project-scoped skills (e.g. <cwd>/.elyra/skills). */
	projectSkillsDir: string;
	/** Whether auto-skills is enabled. Read fresh on each call so toggles take effect immediately. */
	getAutoSkills: () => boolean;
	/** Custom operations (default: local filesystem). */
	operations?: SkillWriteOperations;
}

/**
 * Validate a skill name against the Agent Skills spec.
 * Returns an error message, or null if valid.
 */
export function validateSkillName(name: string): string | null {
	if (!name) return "name is required";
	if (name.length > MAX_NAME_LENGTH) {
		return `name exceeds ${MAX_NAME_LENGTH} characters (${name.length})`;
	}
	if (!/^[a-z0-9-]+$/.test(name)) {
		return "name must contain only lowercase letters, digits, and hyphens";
	}
	if (name.startsWith("-") || name.endsWith("-")) {
		return "name must not start or end with a hyphen";
	}
	return null;
}

/** Validate a skill description against the spec. Returns an error message, or null if valid. */
export function validateSkillDescription(description: string): string | null {
	if (!description?.trim()) return "description is required";
	if (description.length > MAX_DESCRIPTION_LENGTH) {
		return `description exceeds ${MAX_DESCRIPTION_LENGTH} characters (${description.length})`;
	}
	return null;
}

/** Escape a YAML frontmatter string value. */
function yamlString(value: string): string {
	// Use double quotes and escape backslashes and quotes; collapse newlines.
	const oneLine = value.replace(/\r?\n/g, " ").trim();
	const escaped = oneLine.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
	return `"${escaped}"`;
}

/** Build the full SKILL.md content from frontmatter fields plus a Markdown body. */
export function buildSkillMarkdown(name: string, description: string, body: string): string {
	const frontmatter = ["---", `name: ${name}`, `description: ${yamlString(description)}`, "---", ""].join("\n");
	const trimmedBody = body.replace(/^\n+/, "").replace(/\s+$/, "");
	return `${frontmatter}\n${trimmedBody}\n`;
}

/**
 * Ask the user to approve saving a proposed skill.
 * Returns true to save, false to reject. Falls back to true when no UI is present.
 */
async function confirmSave(
	ctx: ExtensionContext | undefined,
	name: string,
	scope: "user" | "project",
	description: string,
): Promise<boolean> {
	if (!ctx?.hasUI) return true;

	const result = await ctx.ui.custom<boolean>((tui, theme, _kb, done) => {
		let index = 0; // 0 = Save, 1 = Reject
		const options = ["Save skill", "Discard"];
		let cachedLines: string[] | undefined;

		function refresh() {
			cachedLines = undefined;
			tui.requestRender();
		}

		function handleInput(data: string) {
			// Up/down arrows
			if (data === "\x1b[A" || data === "\x1b[B") {
				index = index === 0 ? 1 : 0;
				refresh();
				return;
			}
			// Enter
			if (data === "\r" || data === "\n") {
				done(index === 0);
				return;
			}
			// Escape rejects
			if (data === "\x1b") {
				done(false);
			}
		}

		function render(width: number): string[] {
			if (cachedLines) return cachedLines;
			const lines: string[] = [];
			const rule = "\u2500".repeat(width);
			lines.push(theme.fg("accent", rule));
			lines.push(theme.fg("text", ` Elyra learned something. Save it as a reusable skill?`));
			lines.push("");
			lines.push(` ${theme.fg("accent", name)} ${theme.fg("muted", `(${scope})`)}`);
			lines.push(` ${theme.fg("muted", description)}`);
			lines.push("");
			for (let i = 0; i < options.length; i++) {
				const selected = i === index;
				const prefix = selected ? theme.fg("accent", "> ") : "  ";
				const label = selected ? theme.fg("accent", options[i]) : theme.fg("text", options[i]);
				lines.push(prefix + label);
			}
			lines.push("");
			lines.push(theme.fg("dim", " \u2191\u2193 navigate \u2022 Enter to confirm \u2022 Esc to discard"));
			lines.push(theme.fg("accent", rule));
			cachedLines = lines;
			return lines;
		}

		return {
			render,
			invalidate: () => {
				cachedLines = undefined;
			},
			handleInput,
		};
	});

	return result === true;
}

export function createSkillWriteToolDefinition(
	options: SkillWriteToolOptions,
): ToolDefinition<typeof skillWriteSchema, SkillWriteToolDetails | undefined> {
	const ops = options.operations ?? defaultSkillWriteOperations;

	return {
		name: "skill_write",
		label: "skill write",
		description:
			"Save a hard-won solution as a reusable skill so future sessions never re-solve it. Use this AFTER you have solved a genuinely difficult or non-obvious problem that is likely to recur: a tricky setup, a project-specific workflow, a subtle integration, or a debugging insight. Do NOT use it for routine edits or one-off tasks. The skill becomes available in the next session.",
		promptSnippet: "Save a reusable skill from a hard-won solution",
		promptGuidelines: [
			"Only write a skill when the solution was non-obvious and is likely to recur. Avoid skill spam.",
			"Write the body so a future session can follow it without rediscovering the solution.",
			"Prefer 'project' scope for project-specific workflows, 'user' scope for general techniques.",
		],
		parameters: skillWriteSchema,
		async execute(_toolCallId, params, signal, _onUpdate, ctx) {
			const { name, description, body } = params;
			const scope: "user" | "project" = params.scope === "project" ? "project" : "user";
			const baseDetails: SkillWriteToolDetails = { name, scope, path: null, saved: false };

			const nameError = validateSkillName(name);
			if (nameError) throw new Error(`Invalid skill name: ${nameError}`);

			const descError = validateSkillDescription(description);
			if (descError) throw new Error(`Invalid skill description: ${descError}`);

			if (!body?.trim()) {
				throw new Error("Skill body is required and must not be empty.");
			}

			const skillsRoot = scope === "project" ? options.projectSkillsDir : options.userSkillsDir;
			const skillDir = join(skillsRoot, name);
			const skillPath = join(skillDir, "SKILL.md");

			if (ops.exists(skillPath)) {
				throw new Error(
					`A skill named "${name}" already exists at ${skillPath}. Choose a different name or edit the existing file directly.`,
				);
			}

			if (signal?.aborted) {
				throw new Error("Operation aborted.");
			}

			// Approval: skip when auto-skills is on or no UI is available.
			if (!options.getAutoSkills()) {
				const approved = await confirmSave(ctx, name, scope, description);
				if (!approved) {
					return {
						content: [{ type: "text", text: `Skill "${name}" was not saved (user declined).` }],
						details: { ...baseDetails, rejected: true },
					};
				}
			}

			if (signal?.aborted) {
				throw new Error("Operation aborted.");
			}

			const markdown = buildSkillMarkdown(name, description, body);
			await ops.mkdir(skillDir);
			await ops.writeFile(skillPath, markdown);

			return {
				content: [
					{
						type: "text",
						text: `Saved skill "${name}" (${scope}) to ${skillPath}. It will be available in the next session.`,
					},
				],
				details: { ...baseDetails, path: skillPath, saved: true },
			};
		},
		renderCall(args, theme, context) {
			const renderArgs = args as { name?: string; scope?: string } | undefined;
			const name = typeof renderArgs?.name === "string" ? renderArgs.name : "...";
			const scope = renderArgs?.scope === "project" ? "project" : "user";
			const text =
				`${theme.fg("toolTitle", theme.bold("skill write"))} ${theme.fg("accent", name)} ` +
				theme.fg("muted", `(${scope})`);
			const component = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			component.setText(text);
			return component;
		},
		renderResult(result, _options, theme, context) {
			const details = result.details as SkillWriteToolDetails | undefined;
			const text = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			if (details?.rejected) {
				text.setText(theme.fg("warning", "Discarded"));
			} else if (details?.saved) {
				text.setText(theme.fg("success", "\u2713 Saved \u2022 active next session"));
			} else if (context.isError) {
				const output = result.content
					.filter((c) => c.type === "text")
					.map((c) => ("text" in c ? c.text : ""))
					.join("\n");
				text.setText(theme.fg("error", output));
			} else {
				text.setText("");
			}
			return text;
		},
	};
}

export function createSkillWriteTool(options: SkillWriteToolOptions): AgentTool<typeof skillWriteSchema> {
	return wrapToolDefinition(createSkillWriteToolDefinition(options));
}
